import { SdkContext } from '../internal'
import type {
  PendingUpvoteTopic,
  UpvoteBoard,
  UpvoteComment,
  UpvoteListParams,
  UpvotePage,
  UpvoteSimilarTopic,
  UpvoteStatus,
  UpvoteTopic,
  UpvoteTopicInput,
  UpvoteVoteResult,
  UpvoteVoter,
  UpvoteVotersPage
} from '@/types/upvotes'

interface CursorPagination {
  hasMore?: boolean
  nextCursor?: string | null
  total?: number
}

const itemsOf = <T>(result: unknown): T[] => {
  if (Array.isArray(result)) return result as T[]
  const r = result as { items?: T[]; data?: T[] } | null
  return r?.items || r?.data || []
}

const paginationOf = (result: unknown): CursorPagination =>
  ((result as { pagination?: CursorPagination } | null)?.pagination) ?? {}

/**
 * F-139 Upvote. Every route lives under `/upvotes` with the community from
 * X-CommunityId. Reading (board, topics, similar, detail, comments) works
 * without login in a `public` space; every write needs one (401).
 * Paths are written by hand (F-03).
 */
export class UpvotesResource {
  constructor(private ctx: SdkContext) {}

  async getBoard(spaceId: string): Promise<UpvoteBoard> {
    return this.ctx.call({ httpMethod: 'GET', path: '/upvotes/spaces/{spaceId}' }, { spaceId })
  }

  /** Prior approval of the board (`Space.requireApproval`), owner and admin. */
  async updateBoard(spaceId: string, body: { requireApproval: boolean }): Promise<UpvoteBoard> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/upvotes/spaces/{spaceId}', hasBody: true }, { spaceId, body })
  }

  async listTopics(spaceId: string, params: UpvoteListParams = {}): Promise<UpvotePage> {
    const { sort, status, mine, voted, cursor, limit } = params
    const result = await this.ctx.raw(
      { httpMethod: 'GET', path: '/upvotes/spaces/{spaceId}/topics' },
      {
        spaceId,
        query: {
          sort,
          status: status && status.length ? status.join(',') : undefined,
          mine: mine || undefined,
          voted: voted || undefined,
          cursor: cursor || undefined,
          limit
        }
      }
    )
    const pagination = paginationOf(result)
    return { items: itemsOf<UpvoteTopic>(result), hasMore: Boolean(pagination.hasMore), nextCursor: pagination.nextCursor ?? null }
  }

  /** Up to 5 topics sharing words with a title; `[]` under 3 words. */
  async similar(spaceId: string, title: string): Promise<UpvoteSimilarTopic[]> {
    return itemsOf<UpvoteSimilarTopic>(
      await this.ctx.call({ httpMethod: 'GET', path: '/upvotes/spaces/{spaceId}/similar' }, { spaceId, query: { title } })
    )
  }

  async createTopic(spaceId: string, body: UpvoteTopicInput): Promise<UpvoteTopic> {
    return this.ctx.call({ httpMethod: 'POST', path: '/upvotes/spaces/{spaceId}/topics', hasBody: true }, { spaceId, body })
  }

  async getTopic(id: string): Promise<UpvoteTopic> {
    return this.ctx.call({ httpMethod: 'GET', path: '/upvotes/topics/{id}' }, { id })
  }

  async updateTopic(id: string, body: Partial<UpvoteTopicInput>): Promise<UpvoteTopic> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/upvotes/topics/{id}', hasBody: true }, { id, body })
  }

  async deleteTopic(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/upvotes/topics/{id}' }, { id })
  }

  async vote(id: string): Promise<UpvoteVoteResult> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/upvotes/topics/{id}/vote' }, { id })
  }

  async unvote(id: string): Promise<UpvoteVoteResult> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/upvotes/topics/{id}/vote' }, { id })
  }

  /** Owner, admin and moderator. */
  async listVoters(id: string, cursor?: string | null): Promise<UpvoteVotersPage> {
    const result = await this.ctx.raw(
      { httpMethod: 'GET', path: '/upvotes/topics/{id}/voters' },
      { id, query: { cursor: cursor || undefined, limit: 50 } }
    )
    const pagination = paginationOf(result)
    return {
      items: itemsOf<UpvoteVoter>(result),
      hasMore: Boolean(pagination.hasMore),
      nextCursor: pagination.nextCursor ?? null,
      total: typeof pagination.total === 'number' ? pagination.total : null
    }
  }

  async listComments(id: string): Promise<UpvoteComment[]> {
    return itemsOf<UpvoteComment>(await this.ctx.call({ httpMethod: 'GET', path: '/upvotes/topics/{id}/comments' }, { id }))
  }

  async addComment(id: string, content: string, parentId?: string | null): Promise<UpvoteComment> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/upvotes/topics/{id}/comments', hasBody: true },
      { id, body: { content, ...(parentId ? { parentId } : {}) } }
    )
  }

  async deleteComment(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/upvotes/comments/{id}' }, { id })
  }

  /** The note is required for ARCHIVED (the reason). */
  async setStatus(id: string, status: UpvoteStatus, note?: string): Promise<UpvoteTopic> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/upvotes/topics/{id}/status', hasBody: true },
      { id, body: { status, ...(note ? { note } : {}) } }
    )
  }

  async reopen(id: string, note?: string): Promise<UpvoteTopic> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/upvotes/topics/{id}/reopen', hasBody: true },
      { id, body: note ? { note } : {} }
    )
  }

  async merge(id: string, targetId: string): Promise<{ source: UpvoteTopic; target: UpvoteTopic }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/upvotes/topics/{id}/merge', hasBody: true }, { id, body: { targetId } })
  }

  /* -------------------------------------------------------- moderation -- */

  async listPending(): Promise<PendingUpvoteTopic[]> {
    return itemsOf<PendingUpvoteTopic>(
      await this.ctx.raw({ httpMethod: 'GET', path: '/moderation/upvotes/pending' }, { query: { page: 1, limit: 100 } })
    )
  }

  async moderate(id: string, status: 'APPROVED' | 'REJECTED', rejectionReason?: string): Promise<void> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/moderation/upvotes/{id}', hasBody: true },
      { id, body: { status, ...(rejectionReason ? { rejectionReason } : {}) } }
    )
  }
}
